import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import {
  setDuration,
  addAttraction,
  removeAttraction,
  addTransportation,
  removeTransportation,
  setAttractions,
  setTransportation,
  setDayPeriod,
  setPaymentPreference,
  setPhysicalActivity,
  setPublic
} from "@/features/guideCreation/guideCreationSlice";

// Material UI imports
import {
  Typography,
  Box,
  FormControl,
  Slider,
  TextField,
  Autocomplete,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  CircularProgress
} from '@mui/material';

interface Attraction {
  id: number;
  name: string;
}

interface Transport {
  id: number;
  name: string;
}

interface GuideOptionsProps {
  attractionTypes: Attraction[];
  transportationOptions: Transport[];
  loadingOptions: boolean;
  generating: boolean;
}

const GuideOptionsMUI = ({
  attractionTypes,
  transportationOptions,
  loadingOptions,
  generating,
}: GuideOptionsProps) => {
  const dispatch = useAppDispatch();
  const {
    duration,
    attractions,
    transports,
    day_period,
    payment_preference,
    physical_activity,
    public: isPublic
  } = useAppSelector(state => state.guideCreation);

  const handleDurationChange = (event: Event, newValue: number | number[]) => {
    dispatch(setDuration(newValue as number));
  };

  const handleAttractionChange = (event: React.SyntheticEvent, value: Attraction[]) => {
    dispatch(setAttractions(value.map(attraction => attraction.id)));
  };

  const handleTransportationChange = (event: React.SyntheticEvent, value: Transport[]) => {
    dispatch(setTransportation(value.map(transport => transport.id)));
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        Guide Options
      </Typography>

      {/* Day Period */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="day-period-label">Day Period</InputLabel>
        <Select
          labelId="day-period-label"
          id="day-period"
          value={day_period}
          label="Day Period"
          disabled={generating}
          onChange={(e) => dispatch(setDayPeriod(e.target.value))}
        >
          <MenuItem value="Morning">Morning</MenuItem>
          <MenuItem value="Afternoon">Afternoon</MenuItem>
          <MenuItem value="Evening">Evening</MenuItem>
          <MenuItem value="All day">All Day</MenuItem>
        </Select>
      </FormControl>

      {/* Duration */}
      <Box sx={{ mb: 3 }}>
        <Typography gutterBottom>
            Tour Length (approximately)
        </Typography>
        <Slider
          value={duration}
          onChange={handleDurationChange}
          step={1}
          marks
          min={1}
          max={12}
          disabled={generating}
          valueLabelDisplay="auto"
        />
        <FormHelperText>
            <b>{duration}</b> {duration === 1 ? 'hour' : 'hours'}
        </FormHelperText>
      </Box>

      {/* Attractions */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <Autocomplete
          multiple
          id="attractions"
          options={attractionTypes}
          value={attractionTypes.filter(attraction => attractions.includes(attraction.id))}
          onChange={handleAttractionChange}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={loadingOptions}
          disabled={loadingOptions || generating}
          renderOption={(props, option) => {
            // Extract key from props to pass it directly
            const { key, ...otherProps } = props;
            return (
              <Box component="li" key={key} {...otherProps}>
                {option.name}
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Attractions"
              placeholder={loadingOptions ? "Loading attractions..." : "Select attractions"}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingOptions ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...chipProps } = getTagProps({ index });
              return (
                <Chip
                  label={option.name}
                  key={key || option.id}
                  {...chipProps}
                />
              );
            })
          }
        />
      </FormControl>

      {/* Transportation */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <Autocomplete
          multiple
          id="transportation"
          options={transportationOptions}
          value={transportationOptions.filter(transport => transports.includes(transport.id))}
          onChange={handleTransportationChange}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={loadingOptions}
          disabled={loadingOptions || generating}
          renderOption={(props, option) => {
            // Extract key from props to pass it directly
            const { key, ...otherProps } = props;
            return (
              <Box component="li" key={key} {...otherProps}>
                {option.name}
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Transportation"
              placeholder={loadingOptions ? "Loading transportation options..." : "Select transportation options"}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingOptions ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...chipProps } = getTagProps({ index });
              return (
                <Chip
                  label={option.name}
                  key={key || option.id}
                  {...chipProps}
                />
              );
            })
          }
        />
      </FormControl>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="payment-preference-label">Payment Preference</InputLabel>
        <Select
          labelId="payment-preference-label"
          id="payment-preference"
          value={payment_preference}
          label="Payment Preference"
          disabled={generating}
          onChange={(e) => dispatch(setPaymentPreference(e.target.value))}
        >
          <MenuItem value="Free">Free</MenuItem>
          <MenuItem value="Paid">Paid</MenuItem>
          <MenuItem value="Both">Both Free & Paid</MenuItem>
        </Select>
      </FormControl>

      {/*<FormControl fullWidth sx={{ mb: 3 }}>*/}
      {/*  <InputLabel id="physical-activity-label">Physical Activity Level</InputLabel>*/}
      {/*  <Select*/}
      {/*    labelId="physical-activity-label"*/}
      {/*    id="physical-activity"*/}
      {/*    value={physical_activity}*/}
      {/*    label="Physical Activity Level"*/}
      {/*    disabled={generating}*/}
      {/*    onChange={(e) => dispatch(setPhysicalActivity(e.target.value))}*/}
      {/*  >*/}
      {/*    <MenuItem value="Low">Low</MenuItem>*/}
      {/*    <MenuItem value="Moderate">Moderate</MenuItem>*/}
      {/*    <MenuItem value="High">High</MenuItem>*/}
      {/*  </Select>*/}
      {/*</FormControl>*/}

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="public-label">Make Guide Public</InputLabel>
        <Select
          labelId="public-label"
          id="public"
          value={isPublic ? "true" : "false"}
          label="Make Guide Public"
          disabled={generating}
          onChange={(e) => dispatch(setPublic(e.target.value === "true"))}
        >
          <MenuItem value="true">Yes, make it public</MenuItem>
          <MenuItem value="false">No, keep it private</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

export default GuideOptionsMUI;
